// Path helpers operating on the sampleData array shape.
//
// Paths address values inside an array of `pageContent`-keyed objects.
// The first segment is the `pageContent` discriminator (e.g. "aboutPage").
// For pageContent values that appear multiple times (e.g. "standardDeviationSection"),
// use `pageContent[<subKey>]` where the sub-key matches a secondary discriminator
// (currently `dimension`). Array indices use `[i]` notation.
//
// Examples:
//   "aboutPage.calloutHeadline"
//   "firstPage.tableContents.contents[2][3]"
//   "standardDeviationSection[Collaboration].dimensionLabel"
//   "firstPage.subHeading.line1"   <- synthetic; see SYNTHETIC_PATHS below

const SUB_DISCRIMINATORS = {
  standardDeviationSection: 'dimension',
};

function parseSegments(path) {
  const segments = [];
  const re = /([^.\[\]]+)|\[([^\]]+)\]/g;
  let match;
  while ((match = re.exec(path)) !== null) {
    segments.push(match[1] !== undefined ? match[1] : match[2]);
  }
  return segments;
}

function findPageEntry(data, pageContent, subKey) {
  if (subKey != null) {
    const discr = SUB_DISCRIMINATORS[pageContent];
    if (discr) {
      return data.find(
        d => d.pageContent === pageContent && d[discr] === subKey
      );
    }
  }
  return data.find(d => d.pageContent === pageContent);
}

function walk(obj, segments) {
  let cur = obj;
  for (const seg of segments) {
    if (cur == null) return undefined;
    cur = cur[seg];
  }
  return cur;
}

function setWalk(obj, segments, value) {
  if (segments.length === 0) return value;
  const [head, ...rest] = segments;
  if (rest.length === 0) {
    const copy = Array.isArray(obj) ? obj.slice() : { ...obj };
    copy[head] = value;
    return copy;
  }
  const next = obj && obj[head];
  const childIsArray = !isNaN(parseInt(rest[0], 10));
  const child = next != null ? next : (childIsArray ? [] : {});
  const newChild = setWalk(child, rest, value);
  const copy = Array.isArray(obj) ? obj.slice() : { ...obj };
  copy[head] = newChild;
  return copy;
}

// Synthetic paths split/rejoin a backing string so the existing storage format
// is preserved (e.g. firstPage.subHeading is a "\n"-joined multi-line string;
// only the first line — the report title — is editable).
function getSynthetic(data, path) {
  if (path === 'firstPage.subHeading.line1') {
    const entry = findPageEntry(data, 'firstPage');
    if (!entry || !entry.subHeading) return undefined;
    return entry.subHeading.split('\n')[0].trim();
  }
  return undefined;
}

function setSynthetic(data, path, value) {
  if (path === 'firstPage.subHeading.line1') {
    const idx = data.findIndex(d => d.pageContent === 'firstPage');
    if (idx === -1) return data;
    const cur = data[idx].subHeading || '';
    const parts = cur.split('\n');
    parts[0] = value;
    const next = data.slice();
    next[idx] = { ...next[idx], subHeading: parts.join('\n') };
    return next;
  }
  return data;
}

const SYNTHETIC_PATHS = new Set(['firstPage.subHeading.line1']);

export function getByPath(data, path) {
  if (SYNTHETIC_PATHS.has(path)) return getSynthetic(data, path);
  const segments = parseSegments(path);
  if (segments.length === 0) return undefined;
  const [pageContent, ...rest] = segments;
  let subKey = null;
  let restSegments = rest;
  if (rest.length > 0 && SUB_DISCRIMINATORS[pageContent]) {
    subKey = rest[0];
    restSegments = rest.slice(1);
  }
  const entry = findPageEntry(data, pageContent, subKey);
  if (!entry) return undefined;
  return walk(entry, restSegments);
}

export function setByPath(data, path, value) {
  if (SYNTHETIC_PATHS.has(path)) return setSynthetic(data, path, value);
  const segments = parseSegments(path);
  if (segments.length === 0) return data;
  const [pageContent, ...rest] = segments;
  let subKey = null;
  let restSegments = rest;
  if (rest.length > 0 && SUB_DISCRIMINATORS[pageContent]) {
    subKey = rest[0];
    restSegments = rest.slice(1);
  }
  const idx = data.findIndex(d => {
    if (d.pageContent !== pageContent) return false;
    if (subKey == null) return true;
    const discr = SUB_DISCRIMINATORS[pageContent];
    return d[discr] === subKey;
  });
  if (idx === -1) return data;
  const next = data.slice();
  next[idx] = setWalk(next[idx], restSegments, value);
  return next;
}
